<template>
  <SlugInput
    v-model="model"
    class="text-black font-weight-medium mt-n3"
    variant="outlined"
    :v-text-field-props="{
      label: labels.PROJECT_CONFIGURATION.PROJECT_SLUG_LABEL,
      prefix: getProjectUrlPrefix(),
      suffix: getProjectUrlSuffix(),
      validateOn: 'eager',
    }"
    :name
    :other-known-slugs
    :loading
  >
    <template #label-all>{{ null }}</template>
  </SlugInput>
</template>
<script lang="ts">
  /**
   * @returns a representative url for the project with the given projectId if provided, or otherwise for the currently navigated-to project
   */
  export const getProjectUrl = (projectId?: string) => {
    const url = new URL(
      useRouter().resolve({
        name: labels.SIDEBAR.MAIN.PROJECT_OVERVIEW,
        params: { projectId },
      }).href,
      window.location.origin,
    )
    return url.href.replace(/^https?:\/\//i, '')
  }
</script>
<script setup lang="ts">
  import { useRouter } from 'vue-router'
  import labels from '@/translations/labels.json'
  import { SlugInput } from '@itemis-solutions/platform-web-components'
  import { without } from 'lodash'
  import { computed } from 'vue'
  import { asyncComputed } from '@vueuse/core'
  import { injectTyped } from '@/inject'
  import { ref } from 'vue'
  import { ServiceKeys } from '@/services/symbols'

  const props = defineProps<{
    name?: string
    /**
     * A slug that is valid to take regardless of known project slugs (i.e. the currently persisted slug value)
     */
    allowed?: string
    organizationIdOrSlug: string | undefined
    loading?: boolean
  }>()

  const model = defineModel<string>()

  const projectService = injectTyped(ServiceKeys.project)

  const knownProjectSlugsLoading = ref(false)
  const loading = computed(() => props.loading || knownProjectSlugsLoading.value)
  const knownProjectSlugs = asyncComputed<string[]>(
    () => {
      if (props.organizationIdOrSlug)
        return projectService
          .getProjectsOfOrganization(props.organizationIdOrSlug)
          .then(
            (projects) =>
              projects.map((project) => project.slug).filter((slug) => !!slug) as string[],
          )
      else return []
    },
    [],
    knownProjectSlugsLoading,
  )

  const otherKnownSlugs = computed(() => without(knownProjectSlugs?.value, props.allowed))

  const getProjectUrlPrefix = () => getProjectUrlParts()?.[0]
  const getProjectUrlSuffix = () => getProjectUrlParts()?.[1]

  const getProjectUrlParts = () => {
    const projectId = 'any-project-id'
    return getProjectUrl(projectId).split(projectId)
  }
</script>
